import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PUBLISHED, UNPUBLISHED,
    TYPE_A, TYPE_B,
    PROVEN_NO, PROVEN_IN_USE, PRIOR_USE,
} from "const"
import {
    StoreType,
    QidModelFilters,
    LangType,
} from "client/store"
import { PublishedUnpublished } from "client/utils"
import {
    FormGroup,
    Label,
    Input,
    SelectDropdown,
    SelectDropdownItem,
    CheckButton,
    CheckboxWrapper,
    Empty,
    Button,
    SearchBar,
    SearchFilter,
} from "client/components"

interface SearchQidModelForm {
    filters: QidModelFilters
    onFilter: (filters: QidModelFilters) => void
}
interface SearchQidModel extends SearchQidModelForm {
    id: string
    leftOffset?: boolean
    rightOffset?: boolean
}

const statusArray = [PUBLISHED, UNPUBLISHED]

const QidModelSearch = ({
    id,
    leftOffset = false,
    rightOffset = false,
    filters,
    onFilter,
}: SearchQidModel) => {
    const lang = useSelector((state: StoreType) => state.system.lang)

    const popupFilters: SearchFilter[] = []
    const extendedFilters: SearchFilter[] = []

    // popup filters
    statusArray.forEach((status: PublishedUnpublished) => {
        popupFilters.push({
            title: lang[status],
            isActive: filters.status === status,
            onAdd: (obj: QidModelFilters) => ({
                ...obj,
                status,
            }),
            onClose: (obj: QidModelFilters) => ({
                ...obj,
                status: null,
            }),
        })
    })

    // extended filters
    if (filters.name && filters.name.length) {
        extendedFilters.push({
            title: `${lang.name}: ${filters.name}`,
            onClose: (obj: QidModelFilters) => ({
                ...obj,
                name: null,
            }),
        })
    }
    if (filters.type) {
        extendedFilters.push({
            title: `${lang.type}: ${filters.type}`,
            onClose: (obj: QidModelFilters) => ({
                ...obj,
                type: null,
            }),
        })
    }
    if (filters.lowDemandMode) {
        extendedFilters.push({
            title: `${lang.mode}: ${lang.modeLowDemand}`,
            onClose: (obj: QidModelFilters) => ({
                ...obj,
                lowDemandMode: null,
            }),
        })
    }
    if (filters.highDemandMode) {
        extendedFilters.push({
            title: `${lang.mode}: ${lang.modeHighDemand}`,
            onClose: (obj: QidModelFilters) => ({
                ...obj,
                highDemandMode: null,
            }),
        })
    }
    if (filters.continuousMode) {
        extendedFilters.push({
            title: `${lang.mode}: ${lang.modeContinuous}`,
            onClose: (obj: QidModelFilters) => ({
                ...obj,
                continuousMode: null,
            }),
        })
    }
    if (filters.logicSolver === false || filters.logicSolver === true) {
        extendedFilters.push({
            title: `${lang.logicSolver}: ${filters.logicSolver ? lang.yes : lang.no}`,
            onClose: (obj: QidModelFilters) => ({
                ...obj,
                logicSolver: null,
            }),
        })
    }
    if ([PROVEN_NO, PROVEN_IN_USE, PRIOR_USE].indexOf(filters.provenInUse) >= 0) {
        let proven = ""
        if (filters.provenInUse === PROVEN_NO) proven = lang.no
        if (filters.provenInUse === PROVEN_IN_USE) proven = lang.provenInUse
        if (filters.provenInUse === PRIOR_USE) proven = lang.priorUse

        extendedFilters.push({
            title: `${lang.provenReliabilityData}: ${proven}`,
            onClose: (obj: QidModelFilters) => ({
                ...obj,
                provenInUse: null,
            }),
        })
    }

    return (
        <SearchBar
            id={id}
            filters={filters}
            leftOffset={leftOffset}
            rightOffset={rightOffset}
            popupFilters={popupFilters}
            extendedFilters={extendedFilters}
            filterForm={<QidModelFilterForm filters={filters} onFilter={onFilter} />}
            onFilter={onFilter}
        />
    )
}
export default QidModelSearch


const QidModelFilterForm = ({
    filters,
    onFilter,
}: SearchQidModelForm) => {
    const lang = useSelector((state: StoreType) => state.system.lang)

    const [name, setName] = useState(filters.name || "")
    const [type, setType] = useState(filters.type)
    const [lowDemandMode, setLowDemandMode] = useState(filters.lowDemandMode)
    const [highDemandMode, setHighDemandMode] = useState(filters.highDemandMode)
    const [continuousMode, setContinuousMode] = useState(filters.continuousMode)
    const [logicSolver, setLogicSolver] = useState(filters.logicSolver)
    const [provenInUse, setProvenInUse] = useState(filters.provenInUse)
    const [status, setStatus] = useState(filters.status)

    useEffect(() => {
        setName(filters.name || "")
        setType(filters.type)
        setLowDemandMode(filters.lowDemandMode)
        setHighDemandMode(filters.highDemandMode)
        setContinuousMode(filters.continuousMode)
        setLogicSolver(filters.logicSolver)
        setProvenInUse(filters.provenInUse)
        setStatus(filters.status)
    }, [filters])

    const onSearch = () => {
        const newFilters = {
            ...filters,
            name,
            type,
            lowDemandMode,
            highDemandMode,
            continuousMode,
            logicSolver,
            provenInUse,
            status,
        }
        onFilter(newFilters)
    }

    let provenTitle = ""
    if (provenInUse === PROVEN_NO) provenTitle = lang.no
    if (provenInUse === PROVEN_IN_USE) provenTitle = lang.provenInUse
    if (provenInUse === PRIOR_USE) provenTitle = lang.priorUse

    return (
        <Row>
            <Col xs={12}>
                <FormGroup>
                    <Label>{lang.name}</Label>
                    <Input type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </FormGroup>
                <FormGroup>
                    <Label>{lang.type}</Label>
                    <SelectDropdown title={type || <Empty />}>
                        <SelectDropdownItem onClick={() => setType(null)}><Empty /></SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setType(TYPE_A)}>{TYPE_A}</SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setType(TYPE_B)}>{TYPE_B}</SelectDropdownItem>
                    </SelectDropdown>
                </FormGroup>
                <FormGroup>
                    <Label>{lang.mode}</Label>
                    <CheckboxWrapper>
                        <Col xs={12} md={4}>
                            <CheckButton
                                checked={lowDemandMode}
                                onChange={() => setLowDemandMode(!lowDemandMode)}
                                title={lang.modeLowDemand}
                            />
                        </Col>
                        <Col xs={12} md={4}>
                            <CheckButton
                                checked={highDemandMode}
                                onChange={() => setHighDemandMode(!highDemandMode)}
                                title={lang.modeHighDemand}
                            />
                        </Col>
                        <Col xs={12} md={4}>
                            <CheckButton
                                checked={continuousMode}
                                onChange={() => setContinuousMode(!continuousMode)}
                                title={lang.modeContinuous}
                            />
                        </Col>
                    </CheckboxWrapper>
                </FormGroup>
                <FormGroup>
                    <Label>{lang.logicSolver}</Label>
                    <SelectDropdown title={
                        logicSolver !== false && logicSolver !== true
                            ? <Empty />
                            : logicSolver ? lang.yes : lang.no
                    }
                    >
                        <SelectDropdownItem onClick={() => setLogicSolver(null)}><Empty /></SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setLogicSolver(true)}>{lang.yes}</SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setLogicSolver(false)}>{lang.no}</SelectDropdownItem>
                    </SelectDropdown>
                </FormGroup>
                <FormGroup>
                    <Label>{lang.provenReliabilityData}</Label>
                    <SelectDropdown title={provenTitle || <Empty />}>
                        <SelectDropdownItem onClick={() => setProvenInUse(null)}><Empty /></SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setProvenInUse(PROVEN_NO)}>{lang.no}</SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setProvenInUse(PROVEN_IN_USE)}>{lang.provenInUse}</SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setProvenInUse(PRIOR_USE)}>{lang.priorUse}</SelectDropdownItem>
                    </SelectDropdown>
                </FormGroup>
                <FormGroup>
                    <Label>{lang.status}</Label>
                    <SelectDropdown title={status ? lang[status as keyof LangType] : <Empty />}>
                        <SelectDropdownItem onClick={() => setStatus(null)}><Empty /></SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setStatus(PUBLISHED)}>{lang.published}</SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setStatus(UNPUBLISHED)}>{lang.unpublished}</SelectDropdownItem>
                    </SelectDropdown>
                </FormGroup>
            </Col>
            <Col xs={12}>
                <Button row onClick={onSearch}>{lang.search}</Button>
            </Col>
        </Row>
    )
}
